import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { productUrl } from "@/lib/productUrl";
import { HEATHER_IMG, HEATHER_NAME, STORE_PHONE } from "@/lib/brand";
import {
  X,
  Trash2,
  Sparkles,
  Truck,
  Package,
  HeartHandshake,
  ShieldCheck,
  Lock,
  Flame,
  Phone,
  ArrowRight,
  Heart,
  Wand2,
  Star,
} from "lucide-react";

const FREE_SHIP_THRESHOLD = 150;
const GARMENT_BAG_FEE = 35;
const DONATION_PRESETS = [5, 10, 20];
const EXTRAS_KEY = "hb_cart_extras";

export function SideCart() {
  const { cart, open, closeCart, removeItem, refresh, addItem } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [garmentBag, setGarmentBag] = useState(false);
  const [donation, setDonation] = useState(0);
  const [donationCustom, setDonationCustom] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [weeklyLocks, setWeeklyLocks] = useState(null);
  const [recentTicker, setRecentTicker] = useState([]);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [upsells, setUpsells] = useState([]);

  // Hydrate extras from localStorage so they survive the /cart transition
  useEffect(() => {
    try {
      const raw = localStorage.getItem(EXTRAS_KEY);
      if (raw) {
        const v = JSON.parse(raw);
        setGarmentBag(!!v.garment_bag);
        setDonation(Number(v.donation) || 0);
      }
    } catch { /* ignore */ }
  }, []);

  // Persist extras (garment bag + donation + user's school) whenever they change
  useEffect(() => {
    const school_id = user?.school_id || null;
    const payload = {
      garment_bag: garmentBag,
      donation: Number(donation) || 0,
      donation_school_id: (Number(donation) > 0 ? school_id : null),
    };
    try { localStorage.setItem(EXTRAS_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
  }, [garmentBag, donation, user?.school_id]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeCart(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeCart]);

  // lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // fetch school-lock social proof + rotating ticker + upsells
  useEffect(() => {
    if (!open) return;
    api.schoolLocksWeek(user?.school_id).then(({ data }) => {
      setWeeklyLocks(data);
      if (data?.school_name) setSchoolName(data.school_name);
    }).catch(() => {});
    api.recentLocks(10).then(({ data }) => setRecentTicker(data || [])).catch(() => {});
    // pick 2 upsells from an item's recommendations if any
    if (cart.items[0]?.product_id) {
      api.product(cart.items[0].product_id).then(({ data }) => {
        setUpsells((data.bundle?.length ? data.bundle : data.recommendations || []).slice(0, 3));
      }).catch(() => {});
    }
  }, [open, user?.school_id, cart.items[0]?.product_id]);

  useEffect(() => {
    if (!recentTicker.length) return;
    const id = setInterval(() => setTickerIdx((v) => (v + 1) % recentTicker.length), 3200);
    return () => clearInterval(id);
  }, [recentTicker.length]);

  const subtotal = cart.subtotal || 0;
  const shipFraction = Math.min(1, subtotal / FREE_SHIP_THRESHOLD);
  const shipRemaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const donationAmt = Number(donation) || 0;
  const total = subtotal + (garmentBag ? GARMENT_BAG_FEE : 0) + donationAmt;
  const itemCount = cart.items.reduce((n, i) => n + (i.quantity || 1), 0);

  const bundleDiscount = useMemo(() => {
    // if 2+ items in same tuxedo family, virtual "10% bundle savings" ribbon
    const tuxCount = cart.items.filter((i) => (i.product?.category || "").toLowerCase() === "tuxedos").length;
    if (tuxCount >= 3) return Math.round(subtotal * 0.10);
    return 0;
  }, [cart.items, subtotal]);

  const goCheckout = () => {
    closeCart();
    navigate("/cart");
  };

  const addUpsell = async (p) => {
    // Guest-friendly — addItem in CartContext handles both signed-in and
    // guest (localStorage) modes. No auth wall on the mini-cart upsells.
    try {
      await addItem({ product_id: p.id, size: (p.sizes || [])[0] || "", quantity: 1, color: p.color || null });
    } catch { /* ignore — sold-out or similar */ }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeCart}
            className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm"
            data-testid="side-cart-backdrop"
          />

          {/* Drawer */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            className="fixed top-0 right-0 h-screen w-full sm:w-[440px] bg-ivory z-[81] flex flex-col shadow-2xl"
            data-testid="side-cart"
          >
            {/* ── HEADER — Heather's photo + note ───────────────────── */}
            <div className="relative bg-gradient-to-br from-cream via-blush/40 to-ivory border-b border-cream">
              <button
                onClick={closeCart}
                data-testid="side-cart-close"
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-rosemetal transition-colors z-10"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="p-6 flex items-start gap-4">
                <div className="relative shrink-0">
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-rosemetal/50 to-champagne/40 blur-sm" />
                  <img
                    src={HEATHER_IMG}
                    alt={HEATHER_NAME}
                    className="relative w-16 h-16 rounded-full object-cover object-top border-2 border-cream shadow-md"
                  />
                  <span className="absolute -bottom-1 -right-1 bg-rosemetal text-cream rounded-full w-6 h-6 flex items-center justify-center">
                    <Sparkles className="w-3 h-3" strokeWidth={2} />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-label text-[9px] tracking-[0.25em] text-rosemetal">A NOTE FROM {HEATHER_NAME.toUpperCase()}</p>
                  <p className="font-display italic text-ink text-lg leading-snug mt-1">
                    "You've got great taste. Let's lock this in before someone else does."
                  </p>
                </div>
              </div>

              {/* Free-ship progress bar */}
              <div className="px-6 pb-5">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-label text-[9px] tracking-widest text-ink/70 flex items-center gap-1.5">
                    <Truck className="w-3 h-3" />
                    {shipRemaining === 0 ? "YOU'VE UNLOCKED FREE SHIPPING ✨" : `$${shipRemaining.toFixed(0)} AWAY FROM FREE SHIP`}
                  </span>
                  <span className="font-label text-[9px] text-ink/50">${FREE_SHIP_THRESHOLD}</span>
                </div>
                <div className="h-2 bg-ivory rounded-full overflow-hidden border border-cream">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${shipFraction * 100}%` }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-rosemetal via-rosemetal/90 to-champagne"
                  />
                </div>
              </div>
            </div>

            {/* ── SCHOOL SOCIAL PROOF TICKER ─────────────────────── */}
            {(weeklyLocks?.count > 0 || recentTicker.length > 0) && (
              <div className="bg-ink text-cream px-6 py-2.5 flex items-center gap-2 overflow-hidden">
                <Flame className="w-3.5 h-3.5 text-champagne shrink-0" strokeWidth={1.75} />
                <div className="flex-1 min-w-0 h-5 relative">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={tickerIdx}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 flex items-center font-body text-xs truncate"
                    >
                      {weeklyLocks?.count > 0 && tickerIdx % 2 === 0 ? (
                        <span>
                          <b className="text-champagne">{weeklyLocks.count}</b>{" "}
                          {weeklyLocks.school_name
                            ? <>students at <b className="text-champagne">{weeklyLocks.school_name}</b> locked their look this week</>
                            : <>students in WI locked their look this week</>}
                        </span>
                      ) : recentTicker[tickerIdx % recentTicker.length] ? (
                        <span>
                          <b className="text-champagne">{recentTicker[tickerIdx % recentTicker.length].first_name}</b> just locked{" "}
                          <span className="italic">{recentTicker[tickerIdx % recentTicker.length].product_name?.slice(0, 34)}</span>
                        </span>
                      ) : null}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* ── BODY ─────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto">
              {cart.items.length === 0 ? (
                <EmptyState onClose={closeCart} />
              ) : (
                <div className="p-6 space-y-4">
                  {/* Line items */}
                  {cart.items.map((it, idx) => (
                    <motion.div
                      key={it.product_id + (it.size || "") + (it.color || "")}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      data-testid={`side-cart-item-${idx}`}
                      className="flex gap-3 group"
                    >
                      <Link to={productUrl(it.product, it.color)} onClick={closeCart} className="shrink-0 w-20 aspect-[3/4] bg-blush rounded-sm overflow-hidden">
                        <img src={it.image || it.product?.images?.[0]} alt={it.product?.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={productUrl(it.product, it.color)} onClick={closeCart} className="font-display text-base text-ink leading-tight hover:text-rosemetal line-clamp-2">
                          {it.product?.name}
                        </Link>
                        <p className="font-label text-[9px] text-ink/50 tracking-widest mt-1">
                          {[it.color, it.size ? `Size ${it.size}` : null, it.quantity > 1 ? `×${it.quantity}` : null].filter(Boolean).join(" · ") || "ONE SIZE"}
                        </p>
                        {/* Lock badge if this is a locked dress */}
                        {it.product?.category !== "tuxedos" && it.product?.lockable !== false && (
                          <span className="inline-flex items-center gap-1 font-label text-[8px] text-rosemetal mt-1.5">
                            <Lock className="w-2.5 h-2.5" /> LOCKED AT YOUR SCHOOL
                          </span>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-body text-sm text-ink">${it.line_total}</span>
                          <button
                            onClick={() => removeItem(it.product_id)}
                            data-testid={`side-cart-remove-${idx}`}
                            className="text-ink/40 hover:text-rosemetal transition-colors"
                            aria-label="Remove"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Bundle savings pill */}
                  {bundleDiscount > 0 && (
                    <div className="bg-champagne/40 border border-champagne rounded-sm p-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-rosemetal shrink-0" />
                      <p className="font-body text-xs text-ink">
                        <b>Suit Bundle unlocked</b> — you're saving <b className="text-rosemetal">${bundleDiscount}</b> by pairing pieces. Applied at checkout.
                      </p>
                    </div>
                  )}

                  {/* Custom garment bag toggle — replaces gift wrap */}
                  <button
                    data-testid="side-cart-garment-bag"
                    onClick={() => setGarmentBag((v) => !v)}
                    className={`w-full text-left border rounded-sm p-3 flex items-center gap-3 transition-colors ${
                      garmentBag ? "border-rosemetal bg-blush/40" : "border-cream hover:border-ink/40"
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-sm flex items-center justify-center shrink-0 ${garmentBag ? "bg-rosemetal text-cream" : "border border-ink/30"}`}>
                      {garmentBag && <Sparkles className="w-3 h-3" strokeWidth={2.5} />}
                    </span>
                    <Package className="w-4 h-4 text-rosemetal shrink-0" />
                    <div className="flex-1">
                      <div className="font-body text-sm text-ink">Heather's Custom Garment Bag</div>
                      <div className="font-label text-[9px] text-ink/50 tracking-widest">MONOGRAM-READY · KEEPS YOUR LOOK PRISTINE · +${GARMENT_BAG_FEE}</div>
                    </div>
                  </button>

                  {/* School donation */}
                  <div
                    data-testid="side-cart-donation"
                    className={`border rounded-sm p-3 transition-colors ${
                      donationAmt > 0 ? "border-rosemetal bg-blush/30" : "border-cream"
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <HeartHandshake className="w-4 h-4 text-rosemetal shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-body text-sm text-ink leading-tight">Donate to {schoolName || "your school"}</div>
                        <div className="font-label text-[9px] text-ink/50 tracking-widest mt-0.5">
                          100% GOES TO {schoolName ? schoolName.toUpperCase() : "YOUR SCHOOL BOOSTERS"}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {DONATION_PRESETS.map((amt) => (
                        <button
                          key={amt}
                          data-testid={`side-cart-donate-${amt}`}
                          onClick={() => { setDonation(donation === amt ? 0 : amt); setDonationCustom(""); }}
                          className={`px-3 py-1.5 font-label text-[10px] tracking-widest rounded-sm border transition-colors ${
                            donation === amt
                              ? "border-rosemetal bg-rosemetal text-cream"
                              : "border-cream text-ink hover:border-ink/40"
                          }`}
                        >
                          ${amt}
                        </button>
                      ))}
                      <div className="flex items-center gap-1 border border-cream rounded-sm overflow-hidden">
                        <span className="pl-2 font-label text-[10px] text-ink/60">$</span>
                        <input
                          data-testid="side-cart-donate-custom"
                          type="number"
                          min="0"
                          step="1"
                          placeholder="Other"
                          value={donationCustom}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setDonationCustom(raw);
                            const n = Math.max(0, Math.floor(Number(raw) || 0));
                            setDonation(n);
                          }}
                          className="w-16 py-1.5 font-body text-xs outline-none bg-transparent"
                        />
                      </div>
                      {donationAmt > 0 && (
                        <button
                          onClick={() => { setDonation(0); setDonationCustom(""); }}
                          data-testid="side-cart-donate-clear"
                          className="font-label text-[9px] text-ink/50 tracking-widest hover:text-rosemetal ml-1"
                        >
                          CLEAR
                        </button>
                      )}
                    </div>
                    {donationAmt > 0 && !user?.school_id && (
                      <p className="font-label text-[9px] text-rosemetal/80 tracking-widest mt-2">
                        LINK YOUR SCHOOL IN YOUR ACCOUNT SO WE ROUTE IT CORRECTLY
                      </p>
                    )}
                  </div>

                  {/* Gift note */}
                  <div>
                    <button
                      onClick={() => setShowNote((v) => !v)}
                      className="font-label text-[9px] tracking-widest text-ink/60 hover:text-rosemetal flex items-center gap-1.5"
                      data-testid="side-cart-note-toggle"
                    >
                      <Heart className="w-3 h-3" /> {showNote ? "HIDE NOTE" : "+ ADD A GIFT NOTE"}
                    </button>
                    {showNote && (
                      <textarea
                        data-testid="side-cart-note"
                        rows={2}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="A little something to make it personal…"
                        className="w-full mt-2 border border-cream rounded-sm px-3 py-2 font-body text-xs outline-none focus:border-rosemetal transition-colors"
                      />
                    )}
                  </div>

                  {/* Upsells */}
                  {upsells.length > 0 && (
                    <div className="pt-4 border-t border-cream">
                      <p className="font-label text-[10px] tracking-[0.25em] text-ink/70 mb-3 flex items-center gap-1.5">
                        <Wand2 className="w-3 h-3 text-rosemetal" /> HEATHER PICKED FOR YOU
                      </p>
                      <div className="grid grid-cols-3 gap-2.5">
                        {upsells.map((p) => {
                          const img = p.variants?.[0]?.image || p.images?.[0];
                          return (
                            <div key={p.id} className="group">
                              <div className="aspect-[3/4] bg-blush rounded-sm overflow-hidden mb-1.5 relative">
                                <img src={img} alt={p.name} className="w-full h-full object-cover object-top" />
                                <button
                                  onClick={() => addUpsell(p)}
                                  data-testid={`side-cart-upsell-add-${p.id}`}
                                  className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center font-label text-[9px] tracking-widest text-cream"
                                >
                                  + ADD
                                </button>
                              </div>
                              <p className="font-body text-[10px] text-ink leading-tight line-clamp-2">{p.name}</p>
                              <p className="font-label text-[9px] text-rosemetal">${p.price}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Trust badges */}
                  <div className="pt-4 border-t border-cream grid grid-cols-3 gap-2 text-center">
                    <TrustPill icon={ShieldCheck} label="Secure" />
                    <TrustPill icon={Lock} label="Locked" />
                    <TrustPill icon={Star} label="5.0★ Google" />
                  </div>
                </div>
              )}
            </div>

            {/* ── FOOTER — sticky checkout ─────────────────────── */}
            {cart.items.length > 0 && (
              <div className="border-t border-cream bg-ivory px-6 py-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-label text-[10px] tracking-widest text-ink/60">SUBTOTAL · {itemCount} {itemCount === 1 ? "ITEM" : "ITEMS"}</span>
                  <span className="font-display text-2xl text-ink tabular-nums">${total.toFixed(0)}</span>
                </div>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={goCheckout}
                  data-testid="side-cart-checkout"
                  className="w-full bg-ink text-cream py-4 font-label text-[11px] tracking-[0.25em] rounded-sm hover:bg-rosemetal transition-colors flex items-center justify-center gap-2"
                >
                  <Lock className="w-4 h-4" /> SECURE MY LOOK
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
                <a
                  href={`tel:${STORE_PHONE.replace(/\D/g, "")}`}
                  className="w-full text-center font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal flex items-center justify-center gap-1.5"
                >
                  <Phone className="w-3 h-3" /> QUESTIONS? TEXT HEATHER · {STORE_PHONE}
                </a>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function TrustPill({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1 py-1.5 bg-cream/50 rounded-sm">
      <Icon className="w-3.5 h-3.5 text-rosemetal" strokeWidth={1.75} />
      <span className="font-label text-[8px] tracking-widest text-ink/60">{label}</span>
    </div>
  );
}

function EmptyState({ onClose }) {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center gap-4 py-16">
      <div className="w-24 h-24 rounded-full bg-cream flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-rosemetal" strokeWidth={1.25} />
      </div>
      <p className="font-display text-2xl text-ink">Your bag is empty</p>
      <p className="font-body text-sm text-ink/60 max-w-xs">
        Ready to find your one-of-one? Start with our AI Try-On or browse the collection.
      </p>
      <div className="flex flex-col gap-2 w-full max-w-xs mt-2">
        <Link
          to="/try-on"
          onClick={onClose}
          data-testid="side-cart-empty-try-on"
          className="bg-ink text-cream py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-rosemetal transition-colors"
        >
          TRY ON A LOOK
        </Link>
        <Link
          to="/prom-dresses"
          onClick={onClose}
          className="border border-ink text-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-cream transition-colors"
        >
          SHOP PROM
        </Link>
      </div>
    </div>
  );
}

export default SideCart;
